import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskLines } from "../components/Reveal";
import Marquee from "../components/Marquee";

const CHAPTERS = [
  {
    n: "I",
    title: "Origin",
    body: "Founded in 2019 and headquartered in New Baneshwor, Kathmandu, Dhara Kala Movies began as a small, deliberate company — a room, a light, and a shared belief that Nepali cinema had earned a slower kind of attention. Our name means the stream of art: the current of practice that runs underneath every made thing.",
  },
  {
    n: "II",
    title: "Mission",
    body: "To produce documentaries and cultural films — for private events, festivals and informative programming — that spark reflection and social awareness. From the arrow of Arjun to the caves of Halesi, we build an atlas of the sacred while shining a lens on the questions of contemporary Nepali life.",
  },
  {
    n: "III",
    title: "Craft",
    body: "We favour practical light over engineered light, silence over score, and a single considered gesture over a dozen edited ones. Our sets are quiet. Our directors read. Our lenses breathe.",
  },
  {
    n: "IV",
    title: "Voice",
    body: "We tell Nepali stories in Nepali languages — Nepali, Limbu, Sanskrit, Pali — subtitled where necessary, translated never. The idiom is the film.",
  },
  {
    n: "V",
    title: "Collaboration",
    body: "Since 2019 we have collaborated with leading Nepali production houses — including Aama Saraswati Movies, AASUSen Films, Baasuri Films, Cinema Arts, Picture Frame and Kathmandu Films — to co-produce documentaries that honour the country's cultural and spiritual heritage.",
  },
  {
    n: "VI",
    title: "Future",
    body: "The slate is a season, not a company. If the work is honest, the next chapter will already be writing itself somewhere in the eastern hills.",
  },
];

const COLLABORATORS = [
  { name: "Aama Saraswati Movies", href: "https://np.linkedin.com/in/aama-saraswati-movies-752726307", logo: null },
  { name: "AASUSen Films", href: "https://aasusenfilms.com/", logo: "https://aasusenfilms.com/storage/logo.png" },
  { name: "Baasuri Films", href: "https://baasurifilms.com/", logo: "https://baasurifilms.com/wp-content/uploads/2025/08/cropped-bfp_251-300x66.png" },
  { name: "Cinema Arts", href: "https://cinemaartnepal.com/", logo: null },
  { name: "Picture Frame", href: "https://pictureframenepal.com/", logo: null },
  { name: "Kathmandu Films", href: "https://kathmandufilms.com/", logo: null },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <main data-testid="about-page" className="bg-black">
      {/* Hero image parallax */}
      <section ref={ref} className="relative min-h-[70vh] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
          <img
            src="https://images.unsplash.com/photo-1724154353955-525f6e738c0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxidWRkaGElMjBzaWxob3VldHRlJTIwZ29sZGVuJTIwbGlnaHQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODU1OTc1MzJ8MA&ixlib=rb-4.1.0&q=85"
            alt="About Dhara Kala Movies"
            className="w-full h-[115%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/50 to-black" />
        </motion.div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-8">About Us · Est. 2019 · Kathmandu</p>
          <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter max-w-5xl">
            <MaskLines lines={["The stream", "of art,"]} />
            <span className="block italic text-[color:var(--dk-gold)]">
              <MaskLines lines={["flowing quietly."]} delay={0.4} />
            </span>
          </h1>

          {/* Studio facts strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-4xl" data-testid="about-facts">
            <div className="border-t border-white/20 pt-4">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2">Established</p>
              <p className="font-serif-display text-white text-2xl md:text-3xl">2019</p>
            </div>
            <div className="border-t border-white/20 pt-4">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2">Studio</p>
              <p className="font-serif-display text-white text-lg md:text-xl leading-tight">New Baneshwor<br/>Kathmandu, Nepal</p>
            </div>
            <div className="border-t border-white/20 pt-4">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2">Slate</p>
              <p className="font-serif-display text-white text-2xl md:text-3xl">15+ Projects</p>
            </div>
            <div className="border-t border-white/20 pt-4">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2">Released</p>
              <p className="font-serif-display text-white text-2xl md:text-3xl">6+ Films</p>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto - numbered chapters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20">
          <Reveal className="md:col-span-4">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">Manifesto</p>
          </Reveal>
          <Reveal delay={1} className="md:col-span-8">
            <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl leading-[1] tracking-tight">
              Six chapters, one <span className="italic">intention.</span>
            </h2>
          </Reveal>
        </div>

        <div className="space-y-0">
          {CHAPTERS.map((c, i) => (
            <Reveal key={i} delay={i} className="grid grid-cols-1 md:grid-cols-12 gap-6 border-t border-white/10 py-14 md:py-20">
              <div className="md:col-span-2">
                <span className="font-serif-display italic text-6xl md:text-7xl text-[color:var(--dk-red)] leading-none">
                  {c.n}
                </span>
              </div>
              <div className="md:col-span-3">
                <h3 className="font-serif-display text-3xl md:text-4xl text-white tracking-tight">{c.title}</h3>
              </div>
              <div className="md:col-span-7">
                <p className="text-white/70 leading-relaxed text-lg font-serif-display italic">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee
        items={["Slow cinema", "Sacred geography", "Nepali voices", "Practical light", "The stream of art"]}
        testid="about-marquee"
      />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-40 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <Reveal className="md:col-span-6 md:h-[560px] relative overflow-hidden spotlight-clip">
          <img
            src="https://images.unsplash.com/photo-1727451139462-cd34008cd50b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxjaW5lbWF0aWMlMjBmaWxtJTIwc2V0JTIwZGFyayUyMG1vb2R5JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg1NTk3NTMyfDA&ixlib=rb-4.1.0&q=85"
            alt="On set"
            className="w-full h-full object-cover"
          />
        </Reveal>
        <Reveal delay={1} className="md:col-span-6 md:pl-8">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Vision</p>
          <p className="font-serif-display italic text-white text-3xl md:text-4xl leading-[1.2]">
            We believe cinema is a slow conversation between land and language.
            Every frame we compose is an act of listening — to a river, a chant, a wind,
            the small sound a temple bell makes long after the hand has left it.
          </p>
        </Reveal>
      </section>

      {/* Collaborators / Partner Production Houses */}
      <section className="border-t border-white/10 bg-black" data-testid="about-collaborators">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <Reveal className="md:col-span-4">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]">Collaborators</p>
            </Reveal>
            <Reveal delay={1} className="md:col-span-8">
              <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl leading-[1] tracking-tight">
                Documentaries made <span className="italic text-[color:var(--dk-gold)]">in company.</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-3xl">
                Since 2019, Dhara Kala Movies has partnered with leading Nepali production
                houses to co-produce documentaries and cultural films. Our collaborators
                include:
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-white/10">
            {COLLABORATORS.map((c, i) => (
              <Reveal key={c.name} delay={i % 3}
                data-testid={`collaborator-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="group relative border-b border-r border-white/10 last:border-r-0 min-h-[200px] flex flex-col justify-between hover:bg-[color:var(--dk-muted)] transition-colors">
                <a href={c.href} target="_blank" rel="noopener noreferrer" className="absolute inset-0 z-10"><span className="sr-only">{c.name}</span></a>
                <div className="p-6 flex items-start justify-between">
                  <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]/60">0{i + 1}</span>
                </div>
                <div className="flex-1 flex items-center justify-center px-6">
                  {c.logo ? (
                    <img src={c.logo} alt={c.name} className="max-h-16 max-w-full object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                  ) : (
                    <span className="font-serif-display text-white text-xl md:text-2xl leading-tight text-center group-hover:text-[color:var(--dk-gold)] transition-colors">{c.name}</span>
                  )}
                </div>
                <div className="p-6">
                  <p className="text-[10px] uppercase tracking-overline text-white/40">{c.logo ? c.name : "Documentary Partner · Nepal"}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
